import { useCallback, useMemo, useRef, useState } from 'react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import {
    applyPdfPageEdits,
    pagesHaveEdits,
    MAX_EDIT_PAGES_LARGE,
} from '../shared/pdfPageEditEngine';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfUploadedFile } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import PdfPageEditor from '../../components/pdf/PdfPageEditor';
import { usePdfPageEdits, fileKeyOf } from '../../components/pdf/usePdfPageEdits';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import {
    deriveToolStatus,
    downloadBlob,
    genericHowToSteps,
    getToolStatusText,
} from '../../toolPageUtils';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 500 * 1024 * 1024;

export default function CropPdf() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const clearResult = useCallback(() => {
        setResult(null);
        setError(null);
    }, []);

    const editFiles = useMemo(() => (file ? [file] : []), [file]);
    const { pagesByKey, updatePagesForKey, resetPageEdits } = usePdfPageEdits(editFiles, clearResult, MAX_EDIT_PAGES_LARGE);
    const pages = file ? pagesByKey[fileKeyOf(file)] : null;
    const editable = Array.isArray(pages);
    const hasEdits = editable && pagesHaveEdits(pages);

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        if (!list.length) return;

        const incomingFile = list[0];
        if (!(incomingFile.type === 'application/pdf' || incomingFile.name.toLowerCase().endsWith('.pdf'))) {
            const msg = t('cropPdf.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (incomingFile.size > MAX_FILE_BYTES) {
            const msg = t('cropPdf.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setFile(incomingFile);
        setResult(null);
        setError(null);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in.
    usePdfPaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const clearAll = () => {
        setFile(null);
        setResult(null);
        setError(null);
        resetPageEdits();
    };

    const handleSubmit = async () => {
        if (!file || processing) return;

        if (!hasEdits) {
            const msg = t('cropPdf.errors.noRegion');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        recordToolUsage('pdf/crop', { category: 'pdf' });

        setProcessing(true);
        setResult(null);
        setError(null);

        try {
            const blob = await applyPdfPageEdits(file, pages);
            setResult({
                name: `${file.name.replace(/\.pdf$/i, '')}-cropped.pdf`,
                blob,
            });
            notifications.show({ title: t('common.success'), message: t('cropPdf.done'), color: 'green' });
        } catch (err) {
            let msg = t('cropPdf.errors.failed');
            if (err?.message === 'password') msg = t('cropPdf.errors.password');
            else if (err?.message === 'removeAll') msg = t('cropPdf.errors.removeAll');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
        }
    };

    const canSubmit = !!file && !processing && hasEdits;

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: canSubmit,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => genericHowToSteps(t), [t]);

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('cropPdf.done'),
        error: error || t('toolPage.statusText.error'),
    });
    const successLabel = result ? formatFileSize(result.blob.size) : t('cropPdf.done');
    const fileMeta = file ? formatFileSize(file.size) : '';

    return (
        <ToolPageLayout
            slug="pdf/crop"
            subtitleKey="cropPdf.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={!file}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={file || result || error ? clearAll : undefined}
            primaryAction={{
                label: t('cropPdf.apply'),
                onClick: handleSubmit,
                disabled: !canSubmit || !!result,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    {!file ? (
                        <PdfDropZone
                            title={t('cropPdf.dropTitle')}
                            hint={t('cropPdf.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <PdfUploadedFile
                            name={file.name}
                            meta={fileMeta}
                            onRemove={clearAll}
                            removeLabel={t('cropPdf.removeFile')}
                        />
                    )}
                </>
            )}
            settings={null}
            preview={(
                <ToolPreviewPanel
                    title={t('toolPage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('toolPage.previewEmpty')}
                    idleHint={t('toolPage.previewEmptyHint')}
                    processingLabel={t('cropPdf.processing')}
                    error={error}
                    successLabel={successLabel}
                    downloadLabel={t('cropPdf.download')}
                    onDownload={result ? () => downloadBlob(result.blob, result.name) : undefined}
                    onPrint={result ? () => printBlob(result.blob) : undefined}
                    onEditAgain={result ? clearResult : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={result ? clearAll : undefined}
                    againLabel={t('cropPdf.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('cropPdf.apply')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                    pdfPreview={!!file && !processing && !error}
                >
                    {file && !processing && !error ? (
                        result ? (
                            <PdfDocPreview source={result.blob} />
                        ) : (
                            <PdfPageEditor
                                files={editFiles}
                                pagesByKey={pagesByKey}
                                onPagesChangeForKey={updatePagesForKey}
                                activeIndex={0}
                                onActiveIndexChange={() => {}}
                            />
                        )
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
